import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';

interface AdminTemplate {
  id: string;
  category: string;
  activity: string;
  name: string;
  description: string | null;
  features: string[];
  sessionsCount: number;
  isActive: boolean;
  sortOrder: number;
}

interface TemplateForm {
  category: string;
  activity: string;
  name: string;
  description: string;
  features: string; // one per line
  sessionsCount: number;
  isActive: boolean;
}

const EMPTY_FORM: TemplateForm = {
  category: '',
  activity: '',
  name: '',
  description: '',
  features: '',
  sessionsCount: 1,
  isActive: true,
};

const AdminTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<AdminTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [editing, setEditing] = useState<'new' | string | null>(null);
  const [form, setForm] = useState<TemplateForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    const result = await apiService.getAdminOfferingTemplates();
    if (!result.success) {
      setDenied(true);
      setLoading(false);
      return;
    }
    setTemplates(result.templates || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const openEditor = (tpl?: AdminTemplate, presetCategory?: string, presetActivity?: string) => {
    setError('');
    if (tpl) {
      setEditing(tpl.id);
      setForm({
        category: tpl.category,
        activity: tpl.activity,
        name: tpl.name,
        description: tpl.description || '',
        features: (tpl.features || []).join('\n'),
        sessionsCount: tpl.sessionsCount,
        isActive: tpl.isActive,
      });
    } else {
      setEditing('new');
      setForm({ ...EMPTY_FORM, category: presetCategory || '', activity: presetActivity || '' });
    }
  };

  const closeEditor = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
  };

  const saveTemplate = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    const payload = {
      category: form.category.trim(),
      activity: form.activity.trim(),
      name: form.name.trim(),
      description: form.description.trim(),
      features: form.features
        .split('\n')
        .map((f) => f.trim())
        .filter(Boolean)
        .slice(0, 5),
      sessionsCount: Number(form.sessionsCount),
      isActive: form.isActive,
    };
    const result =
      editing === 'new'
        ? await apiService.createOfferingTemplate(payload)
        : await apiService.updateOfferingTemplate(editing as string, payload);
    setSaving(false);
    if (!result.success) {
      setError(result.error || 'Could not save template.');
      return;
    }
    closeEditor();
    fetchTemplates();
  };

  const deleteTemplate = async (tpl: AdminTemplate) => {
    if (!window.confirm(`Delete "${tpl.name}"? Mentors will no longer see this template.`)) return;
    const result = await apiService.deleteOfferingTemplate(tpl.id);
    if (!result.success) {
      window.alert(result.error || 'Could not delete template.');
      return;
    }
    setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
  };

  const toggleActive = async (tpl: AdminTemplate) => {
    const result = await apiService.updateOfferingTemplate(tpl.id, {
      category: tpl.category,
      activity: tpl.activity,
      name: tpl.name,
      description: tpl.description || '',
      features: tpl.features,
      sessionsCount: tpl.sessionsCount,
      sortOrder: tpl.sortOrder,
      isActive: !tpl.isActive,
    });
    if (!result.success) {
      window.alert(result.error || 'Could not update template.');
      return;
    }
    setTemplates((prev) =>
      prev.map((t) => (t.id === tpl.id ? { ...t, isActive: !tpl.isActive } : t))
    );
  };

  if (denied) {
    return (
      <div className="section">
        <div className="wrap" style={{ maxWidth: 760 }}>
          <div className="card" style={{ maxWidth: 520 }}>
            <h3 style={{ marginBottom: 8 }}>Admin access required</h3>
            <p className="meta">Sign in with an admin account to manage offering templates.</p>
          </div>
        </div>
      </div>
    );
  }

  // Group: category → activity → templates (preserves sortOrder from API)
  const grouped: Array<{ category: string; activities: Array<{ activity: string; items: AdminTemplate[] }> }> = [];
  for (const tpl of templates) {
    let cat = grouped.find((g) => g.category === tpl.category);
    if (!cat) {
      cat = { category: tpl.category, activities: [] };
      grouped.push(cat);
    }
    let act = cat.activities.find((a) => a.activity === tpl.activity);
    if (!act) {
      act = { activity: tpl.activity, items: [] };
      cat.activities.push(act);
    }
    act.items.push(tpl);
  }

  const editorForm = (
    <div className="card" style={{ marginBottom: 20, borderColor: 'var(--cardinal)' }}>
      <h3 style={{ marginBottom: 12 }}>{editing === 'new' ? 'New template' : 'Edit template'}</h3>
      <div className="row3" style={{ marginBottom: 12 }}>
        <div className="field">
          <label htmlFor="tpl-category">Category</label>
          <input
            id="tpl-category"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            placeholder="e.g., Robotics"
            list="tpl-categories"
          />
          <datalist id="tpl-categories">
            {Array.from(new Set(templates.map((t) => t.category))).map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
        <div className="field">
          <label htmlFor="tpl-activity">Activity</label>
          <input
            id="tpl-activity"
            value={form.activity}
            onChange={(e) => setForm({ ...form, activity: e.target.value })}
            placeholder="e.g., FTC — FIRST Tech Challenge"
            list="tpl-activities"
          />
          <datalist id="tpl-activities">
            {Array.from(new Set(templates.map((t) => t.activity))).map((a) => (
              <option key={a} value={a} />
            ))}
          </datalist>
        </div>
        <div className="field">
          <label htmlFor="tpl-sessions">Sessions (60 min each)</label>
          <input
            id="tpl-sessions"
            type="number"
            min={1}
            max={20}
            value={form.sessionsCount}
            onChange={(e) => setForm({ ...form, sessionsCount: Number(e.target.value) })}
          />
        </div>
      </div>
      <div className="field" style={{ marginBottom: 12 }}>
        <label htmlFor="tpl-name">Template name (name the result, not the hours)</label>
        <input
          id="tpl-name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          maxLength={80}
          placeholder="e.g., FTC mock judging session"
        />
      </div>
      <div className="field" style={{ marginBottom: 12 }}>
        <label htmlFor="tpl-description">Description</label>
        <textarea
          id="tpl-description"
          rows={2}
          maxLength={300}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
      <div className="field" style={{ marginBottom: 12 }}>
        <label htmlFor="tpl-features">What's included (one per line, up to 5)</label>
        <textarea
          id="tpl-features"
          rows={4}
          value={form.features}
          onChange={(e) => setForm({ ...form, features: e.target.value })}
          placeholder={'Realistic judge Q&A simulation\nFeedback on presentation and team story'}
        />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={form.isActive}
          onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          style={{ width: 'auto' }}
        />
        <span className="meta">Visible to mentors</span>
      </label>
      {error && (
        <p className="meta" style={{ color: 'var(--cardinal)', marginBottom: 12 }}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-primary btn-sm" onClick={saveTemplate} disabled={saving}>
          {saving ? 'Saving…' : 'Save template'}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={closeEditor} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="section">
      <div className="wrap" style={{ maxWidth: 960 }}>
        <span className="eyebrow" style={{ display: 'block', marginBottom: 8 }}>
          Internal · admin
        </span>
        <h1 style={{ fontSize: 30, marginBottom: 8 }}>Offering templates</h1>
        <p className="meta" style={{ marginBottom: 24 }}>
          {templates.length} template{templates.length !== 1 ? 's' : ''} · these appear as "Start
          from a template" presets in the mentor offerings editor. Hidden templates stay here but
          aren't shown to mentors.
        </p>

        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          <button className="btn btn-primary btn-sm" onClick={() => openEditor()}>
            + New template
          </button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
            <Link className="btn btn-secondary btn-sm" to="/admin/activities">
              Activities
            </Link>
            <Link className="btn btn-secondary btn-sm" to="/admin/users">
              Users
            </Link>
            <Link className="btn btn-secondary btn-sm" to="/admin/ops">
              Verification queue
            </Link>
            <Link className="btn btn-secondary btn-sm" to="/admin/feedback">
              User feedback
            </Link>
          </div>
        </div>

        {editing !== null && editorForm}

        {loading ? (
          <div className="card">
            <p className="meta">Loading templates…</p>
          </div>
        ) : grouped.length === 0 ? (
          <div className="card">
            <p className="meta">No templates yet. Create one to get started.</p>
          </div>
        ) : (
          grouped.map((cat) => (
            <div key={cat.category} style={{ marginBottom: 28 }}>
              <h2 style={{ fontSize: 20, marginBottom: 12 }}>{cat.category}</h2>
              {cat.activities.map((act) => (
                <div className="card" key={act.activity} style={{ marginBottom: 14 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 10,
                      marginBottom: 10,
                    }}
                  >
                    <h3 style={{ fontSize: 16, margin: 0 }}>{act.activity}</h3>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ marginLeft: 'auto' }}
                      onClick={() => openEditor(undefined, cat.category, act.activity)}
                    >
                      + Add to this activity
                    </button>
                  </div>
                  {act.items.map((tpl) => (
                    <div className="list-row" key={tpl.id}>
                      <div style={{ minWidth: 0 }}>
                        <b>{tpl.name}</b>{' '}
                        <span className="meta">
                          · {tpl.sessionsCount} session{tpl.sessionsCount !== 1 ? 's' : ''}
                        </span>
                        {!tpl.isActive && (
                          <span
                            className="tag"
                            style={{
                              marginLeft: 8,
                              color: 'var(--cardinal)',
                              borderColor: 'var(--cardinal)',
                            }}
                          >
                            Hidden
                          </span>
                        )}
                        {tpl.description && (
                          <div className="meta" style={{ marginTop: 2 }}>
                            {tpl.description}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => openEditor(tpl)}>
                          Edit
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => toggleActive(tpl)}>
                          {tpl.isActive ? 'Hide' : 'Show'}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => deleteTemplate(tpl)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminTemplates;
